import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { Chess } from 'chess.js';

// Manages game data subscription, player resolution, chess instance, and board orientation
export const useGameState = (gameId, currentUser, isAdmin) => {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playerData, setPlayerData] = useState(null);
  const [playerTeam, setPlayerTeam] = useState(null);
  const [boardOrientation, setBoardOrientation] = useState('white');
  const [chessPosition, setChessPosition] = useState('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');

  const latestGameRef = useRef(null);
  const chessGameRef = useRef(new Chess());
  const chess = chessGameRef.current;

  // Update chess position when game FEN changes from Firebase
  useEffect(() => {
    if (game?.fen) {
      chess.load(game.fen);
      setChessPosition(game.fen);
    }
  }, [game?.fen, chess]);

  // Update board orientation when playerTeam changes
  useEffect(() => {
    if (isAdmin) {
      setBoardOrientation('white');
    } else if (playerTeam) {
      setBoardOrientation(playerTeam);
    }
  }, [playerTeam, isAdmin]);

  // Real-time listener for game data
  useEffect(() => {
    if (!gameId) {
      setError('No game ID provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    const gameRef = doc(db, 'games', gameId);

    const unsubscribe = onSnapshot(gameRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const gameData = { id: docSnapshot.id, ...docSnapshot.data() };
          setGame(gameData);
          latestGameRef.current = gameData;

          // Find current player in the game
          const whitePlayer = gameData.whiteTeam?.find(p => p.email === currentUser.email);
          const blackPlayer = gameData.blackTeam?.find(p => p.email === currentUser.email);

          if (whitePlayer) {
            setPlayerData(whitePlayer);
            setPlayerTeam('white');
            setError('');
          } else if (blackPlayer) {
            setPlayerData(blackPlayer);
            setPlayerTeam('black');
            setError('');
          }

          setLoading(false);
        } else {
          setError('Game not found');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error fetching game:', err);
        setError('Failed to load game');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [gameId, currentUser, isAdmin]);

  return {
    game,
    loading,
    error,
    playerData,
    playerTeam,
    boardOrientation,
    chessPosition,
    setChessPosition,
    chess,
    latestGameRef,
  };
};
